import { useEffect, useRef, useState } from 'react';

function useInView(threshold = 0.2) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

function useCounter(target: number, duration: number, started: boolean, decimals = 0) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!started) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const val = parseFloat((eased * target).toFixed(decimals));
      setCount(val);
      if (progress < 1) requestAnimationFrame(step);
      else setCount(target);
    };
    requestAnimationFrame(step);
  }, [started, target, duration, decimals]);
  return count;
}

interface Metric {
  icon: string;
  beforeLabel: string;
  afterLabel: string;
  beforeValue: number;
  afterValue: number;
  beforeSuffix: string;
  afterSuffix: string;
  beforePrefix: string;
  afterPrefix: string;
  label: string;
  sublabel: string;
  color: string;
  glow: string;
  decimals?: number;
}

const metrics: Metric[] = [
  {
    icon: 'ri-money-euro-circle-line',
    beforeLabel: 'Pred',
    afterLabel: 'Po 12 mesiacoch',
    beforeValue: 4200,
    afterValue: 17300,
    beforeSuffix: '/mes',
    afterSuffix: '/mes',
    beforePrefix: '€',
    afterPrefix: '€',
    label: 'Mesačné tržby',
    sublabel: '+312% nárast obratu',
    color: 'from-emerald-400 to-teal-400',
    glow: 'bg-emerald-500/8',
  },
  {
    icon: 'ri-user-received-2-line',
    beforeLabel: 'Pred',
    afterLabel: 'Po 12 mesiacoch',
    beforeValue: 280,
    afterValue: 1232,
    beforeSuffix: '/mes',
    afterSuffix: '/mes',
    beforePrefix: '',
    afterPrefix: '',
    label: 'Organická návštevnosť',
    sublabel: '+340% viditeľnosť v Googli',
    color: 'from-orange-400 to-amber-400',
    glow: 'bg-orange-500/8',
  },
  {
    icon: 'ri-shopping-cart-2-line',
    beforeLabel: 'Pred',
    afterLabel: 'Po 12 mesiacoch',
    beforeValue: 0.4,
    afterValue: 3.2,
    beforeSuffix: '%',
    afterSuffix: '%',
    beforePrefix: '',
    afterPrefix: '',
    label: 'Konverzný pomer',
    sublabel: 'z 0.4 % na 3.2 % - 8× lepšie',
    color: 'from-amber-400 to-orange-400',
    glow: 'bg-amber-500/8',
    decimals: 1,
  },
  {
    icon: 'ri-pie-chart-2-line',
    beforeLabel: 'Investícia',
    afterLabel: 'Návratnosť',
    beforeValue: 100,
    afterValue: 820,
    beforeSuffix: '%',
    afterSuffix: '%',
    beforePrefix: '',
    afterPrefix: '',
    label: 'ROI z kampaní',
    sublabel: '€8.20 zisku na každé €1 vynaložené',
    color: 'from-violet-400 to-purple-400',
    glow: 'bg-violet-500/8',
  },
];

function MetricCard({ metric, inView, index }: { metric: Metric; inView: boolean; index: number }) {
  const afterCount = useCounter(metric.afterValue, 2200, inView, metric.decimals ?? 0);
  const beforeCount = useCounter(metric.beforeValue, 1500, inView, metric.decimals ?? 0);

  const improvement = metric.afterValue / metric.beforeValue;

  return (
    <div
      className="relative bg-zinc-900 border border-zinc-800/60 rounded-2xl overflow-hidden group hover:border-zinc-700/60 transition-all duration-300 hover:-translate-y-1"
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      {/* Top bar */}
      <div className={`h-1 bg-gradient-to-r ${metric.color}`} />

      <div className="p-7">
        {/* Icon */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 ${metric.glow} rounded-xl flex items-center justify-center`}>
              <i className={`${metric.icon} text-lg text-transparent bg-clip-text bg-gradient-to-r ${metric.color}`} style={{ WebkitBackgroundClip: 'text', color: 'transparent' }} />
            </div>
            <span className="text-zinc-400 text-sm font-medium">{metric.label}</span>
          </div>
          <div className={`text-xs font-black px-2.5 py-1 rounded-full bg-gradient-to-r ${metric.color} text-zinc-900`}>
            ×{improvement.toFixed(1)}
          </div>
        </div>

        {/* Before / After */}
        <div className="flex items-end gap-6 mb-4">
          {/* Before */}
          <div className="flex flex-col">
            <span className="text-zinc-600 text-xs mb-1">{metric.beforeLabel}</span>
            <span className="text-zinc-500 text-2xl font-black line-through decoration-red-500/60">
              {metric.beforePrefix}{metric.decimals ? beforeCount.toFixed(metric.decimals) : beforeCount.toLocaleString()}{metric.beforeSuffix}
            </span>
          </div>
          <div className="flex items-center gap-2 pb-1">
            <i className="ri-arrow-right-line text-zinc-600 text-lg" />
          </div>
          {/* After */}
          <div className="flex flex-col">
            <span className="text-zinc-400 text-xs mb-1">{metric.afterLabel}</span>
            <span className={`text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r ${metric.color} leading-none`}>
              {metric.afterPrefix}{metric.decimals ? afterCount.toFixed(metric.decimals) : afterCount.toLocaleString()}{metric.afterSuffix}
            </span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden mb-3">
          <div
            className={`h-full rounded-full bg-gradient-to-r ${metric.color} transition-all duration-1500`}
            style={{ width: inView ? '100%' : '12%', transitionDelay: `${400 + index * 100}ms` }}
          />
        </div>

        <p className="text-zinc-600 text-xs font-medium">{metric.sublabel}</p>
      </div>
    </div>
  );
}

export default function CaseMetrics() {
  const { ref, inView } = useInView();

  return (
    <section className="py-24 bg-zinc-950 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-zinc-700/40 to-transparent" />

      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] rounded-full bg-orange-500/4 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div
          ref={ref}
          className={`text-center mb-16 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="inline-flex items-center gap-2 bg-zinc-800/60 border border-zinc-700/40 rounded-full px-4 py-1.5 mb-6">
            <i className="ri-pulse-line text-orange-400 text-sm" />
            <span className="text-zinc-300 text-sm font-semibold">Kľúčové výsledky</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-5 leading-tight">
            Čísla pred a{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">
              po spolupráci
            </span>
          </h2>
          <p className="text-zinc-400 text-lg max-w-xl mx-auto leading-relaxed">
            Každé číslo je overiteľné - priamo z Google Analytics, Google Ads a WooCommerce dashboardu.
          </p>
        </div>

        <div
          className={`grid grid-cols-1 sm:grid-cols-2 gap-5 transition-all duration-700 delay-200 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {metrics.map((metric, i) => (
            <MetricCard key={metric.label} metric={metric} inView={inView} index={i} />
          ))}
        </div>

        {/* Additional smaller stats */}
        <div
          className={`mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 transition-all duration-700 delay-500 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {[
            { icon: 'ri-speed-line', label: 'Rýchlosť načítania', value: '0.9s', note: 'z 8.2s' },
            { icon: 'ri-shopping-bag-3-line', label: 'Opustenie košíka', value: '61%', note: 'z 91%' },
            { icon: 'ri-google-line', label: 'Pozície v Google', value: 'TOP 3', note: '28 kľúčových výrazov' },
            { icon: 'ri-mail-send-line', label: 'E-mail open rate', value: '38%', note: 'priemer odvetvia: 22%' },
          ].map((s) => (
            <div key={s.label} className="bg-zinc-900/60 border border-zinc-800/50 rounded-xl p-4 flex items-center gap-4">
              <div className="w-9 h-9 flex items-center justify-center bg-zinc-800 rounded-lg flex-shrink-0">
                <i className={`${s.icon} text-orange-400 text-base`} />
              </div>
              <div>
                <p className="text-white font-bold text-base leading-none mb-0.5">{s.value}</p>
                <p className="text-zinc-500 text-xs leading-tight">{s.label}</p>
                <p className="text-emerald-500 text-xs font-medium">{s.note}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
